import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Megaphone, Send, RefreshCw, FileText, Mail } from "lucide-react";
import { fmt } from "@/pages/admin/utils/format";


// ---------------------------------------------------------------------------
// Iteration 93 — Admin: promotional broadcast via Resend Marketing Broadcasts
// (ONE Broadcast → dedicated marketing audience; no per-recipient sends)
// ---------------------------------------------------------------------------
const STATUS_AR = {
  completed: "اكتمل", sent: "أُرسل", queued: "في الطابور", draft: "مسودة",
  scheduled: "مجدول", canceled: "ملغي", running: "جارٍ",
};

const MarketingAudiencePanel = ({ info, onSync, onRefresh, busy }) => {
  const ls = info?.last_sync;
  return (
    <div
      data-testid="marketing-audience-panel"
      className="mt-4 rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-4 text-xs text-[#5E5C58] space-y-2"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          جمهور Resend التسويقي: <strong className="text-[#222222]">{info?.name || "..."}</strong>
        </span>
        <span>
          العملاء المؤهّلون محليًا:{" "}
          <strong className="text-[#0A4D3C]" data-testid="marketing-local-eligible">
            {info ? info.local_eligible : "..."}
          </strong>
          {info && (
            <span className={info.fits_limit ? "text-emerald-700" : "text-rose-700"}>
              {" "}/ حد الخطة {info.contact_limit} {info.fits_limit ? "✓" : "✗"}
            </span>
          )}
        </span>
        {info?.resend && (
          <span data-testid="marketing-resend-counts">
            في Resend: <strong className="text-[#222222]">{info.resend.total}</strong>
            {" "}(نشط {info.resend.active} — ملغى الاشتراك {info.resend.unsubscribed})
            {info.live ? "" : " — آخر مزامنة"}
          </span>
        )}
        {info?.pending !== undefined && (
          <span>غير مُزامَن: <strong>{info.pending}</strong></span>
        )}
      </div>
      {ls && (
        <div data-testid="marketing-last-sync">
          آخر مزامنة: {STATUS_AR[ls.status] || ls.status}
          {ls.status === "running" ? ` (تمت ${ls.done ?? 0})` : ""}
          {ls.created !== undefined ? ` — أُضيف ${ls.created}، موجود مسبقًا ${ls.already_present}، فشل ${ls.failed}` : ""}
          {ls.error ? ` — ${ls.error}` : ""}
          {ls.finished_at ? ` — ${fmt(ls.finished_at)}` : ""}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button" size="sm" variant="outline" data-testid="marketing-sync-btn"
          onClick={onSync} disabled={busy || info?.sync_running}
          className="rounded-full border-[#0A4D3C] text-[#0A4D3C]"
        >
          <RefreshCw className="w-3.5 h-3.5 ms-1" />
          {info?.sync_running ? "المزامنة قيد التشغيل..." : "مزامنة الجمهور مع Resend"}
        </Button>
        <Button
          type="button" size="sm" variant="ghost" data-testid="marketing-refresh-live-btn"
          onClick={onRefresh} disabled={busy} className="rounded-full"
        >
          تحديث الأعداد من Resend
        </Button>
      </div>
    </div>
  );
};

const AdminBroadcast = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(null);     // local unique-emails count
  const [marketing, setMarketing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAudience = async () => {
    try {
      const r = await api.get("/admin/broadcast/preview-audience");
      setAudience(r.data?.audience_size ?? 0);
    } catch (err) { console.warn("admin: email broadcast preview-audience failed", err); }
  };

  const loadMarketing = async (live = false) => {
    try {
      const r = await api.get(`/admin/broadcast/marketing-audience${live ? "?live=true" : ""}`);
      setMarketing(r.data);
    } catch (err) {
      if (live) toast.error(extractApiErrorMessage(err) || "تعذّر الاتصال بـ Resend");
      console.warn("admin: marketing-audience failed", err);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/broadcasts");
      setHistory(r.data?.items || []);
    } catch (err) { console.warn("admin: email broadcast history failed", err); }
  };

  useEffect(() => {
    loadAudience();
    loadMarketing(false);
    loadHistory();
  }, []);

  useEffect(() => {
    if (!marketing?.sync_running) return undefined;
    const t = setInterval(() => loadMarketing(false), 4000);
    return () => clearInterval(t);
  }, [marketing?.sync_running]);

  const startSync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/broadcast/sync-audience");
      toast.success("بدأت مزامنة الجمهور في الخلفية");
      setTimeout(() => loadMarketing(false), 1500);
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر بدء المزامنة");
    } finally {
      setBusy(false);
    }
  };

  const run = async (mode) => {
    if (!subject.trim() || !body.trim()) {
      toast.error("يرجى تعبئة الموضوع والمحتوى");
      return;
    }
    if (mode === "send") {
      if (audience === 0) { toast.error("لا يوجد عملاء لإرسال العرض إليهم"); return; }
      if (!window.confirm(
        `سيتم إرسال بث تسويقي واحد عبر Resend إلى جمهور «${marketing?.name || "Awtadk Marketing"}» (${audience} عميل مؤهّل). هل تريد المتابعة؟`,
      )) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        subject: subject.trim(), body: body.trim(), mode,
      });
      setLast(data);
      if (mode === "test") {
        toast[data.ok ? "success" : "error"](
          data.ok ? `أُرسلت نسخة تجريبية إلى ${data.sent_to}` : `لم تُرسل النسخة التجريبية: ${data.result?.error || ""}`,
        );
      } else if (mode === "draft") {
        toast.success(`أُنشئت مسودة في Resend (${data.resend_broadcast_id})`);
        loadHistory();
      } else {
        toast.success(`أُرسل البث إلى ${data.audience_size} جهة اتصال نشطة`);
        setSubject(""); setBody("");
        loadHistory();
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر تنفيذ العملية");
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async (h) => {
    try {
      const { data } = await api.get(`/admin/broadcast/resend-status/${h.resend_broadcast_id}`);
      toast.success(`حالة البث: ${STATUS_AR[data.status] || data.status}`);
      loadHistory();
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر تحديث الحالة");
    }
  };

  const canAct = !busy && subject.trim() && body.trim();

  return (
    <div data-testid="admin-broadcast" className="space-y-6">
      <div className="card-warm p-5" dir="rtl">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-5 h-5 text-[#0A4D3C]" />
          <h3 className="font-heading text-xl text-[#222222]">إرسال عرض ترويجي</h3>
        </div>
        <p className="text-sm text-[#5E5C58] leading-relaxed">
          يُرسل العرض كبثّ تسويقي واحد عبر Resend Broadcasts إلى جمهور أوتادك التسويقي
          (مع تخصيص الاسم ورابط إلغاء الاشتراك تلقائيًا) — وليس كرسائل فردية.
          الجمهور المؤهّل حاليًا:{" "}
          <strong className="text-[#0A4D3C]" data-testid="broadcast-audience-size">
            {audience === null ? "..." : `${audience} عميل`}
          </strong>
        </p>

        <MarketingAudiencePanel
          info={marketing} busy={busy}
          onSync={startSync} onRefresh={() => loadMarketing(true)}
        />

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="bc-subject" className="block text-xs text-[#5E5C58] mb-1">
              موضوع البريد
            </label>
            <Input
              id="bc-subject"
              data-testid="broadcast-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="مثال: عرض خاص بمناسبة تدشين الموقع"
            />
          </div>
          <div>
            <label htmlFor="bc-body" className="block text-xs text-[#5E5C58] mb-1">
              نص الرسالة
            </label>
            <textarea
              id="bc-body"
              data-testid="broadcast-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={20000}
              placeholder="اكتب نص الرسالة هنا. الأسطر الجديدة تُحفظ كما هي عند الإرسال."
              className="w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A4D3C]"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            type="button" variant="outline" data-testid="broadcast-test-btn"
            onClick={() => run("test")} disabled={!canAct}
            className="rounded-full border-[#0A4D3C] text-[#0A4D3C]"
          >
            <Mail className="w-3.5 h-3.5 ms-2" /> نسخة تجريبية لبريدي
          </Button>
          <Button
            type="button" variant="outline" data-testid="broadcast-draft-btn"
            onClick={() => run("draft")} disabled={!canAct}
            className="rounded-full border-[#8C6239] text-[#8C6239]"
          >
            <FileText className="w-3.5 h-3.5 ms-2" /> حفظ كمسودة في Resend
          </Button>
          <Button
            type="button"
            data-testid="broadcast-send-btn"
            onClick={() => run("send")}
            disabled={!canAct || !audience}
            className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          >
            <Send className="w-3.5 h-3.5 ms-2" />
            {busy ? "جاري التنفيذ..." : `إرسال البث للجميع (${audience ?? 0})`}
          </Button>
        </div>

        {last && (
          <div
            data-testid="broadcast-last-result"
            className="mt-4 rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-xs text-[#5E5C58] break-words"
          >
            {last.mode === "test" && `نسخة تجريبية → ${last.sent_to}: ${last.ok ? "أُرسلت" : `فشل (${last.result?.error || ""})`}`}
            {last.mode === "draft" && `مسودة Resend: ${last.resend_broadcast_id} — الحالة: ${STATUS_AR[last.status] || last.status}`}
            {last.mode === "send" && `بث Resend: ${last.resend_broadcast_id} — الحالة: ${STATUS_AR[last.status] || last.status} — نشط ${last.audience_size} / ملغى الاشتراك ${last.audience_unsubscribed}`}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="card-warm p-0 overflow-hidden" data-testid="broadcast-history">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F4EE] text-[#5E5C58]">
              <tr>
                <th className="text-start p-4">الموضوع</th>
                <th className="text-start p-4">القناة/الجمهور</th>
                <th className="text-start p-4">الحالة</th>
                <th className="text-start p-4">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-[#E5E1D8]">
                  <td className="p-4 max-w-md break-words">{h.subject}</td>
                  <td className="p-4 text-xs text-[#5E5C58]">
                    {h.channel === "resend_broadcast" ? (
                      <>
                        <span className="text-[#0A4D3C]">Resend Broadcast{h.mode === "draft" ? " (مسودة)" : ""}</span>
                        {" — "}<strong className="text-[#222222]">{h.audience_size}</strong> نشط
                        <div dir="ltr" className="text-[10px] text-[#8B8780] break-all">{h.resend_broadcast_id}</div>
                      </>
                    ) : (
                      <>
                        <strong className="text-[#222222]">{h.sent ?? 0}</strong>{" / "}{h.audience_size}
                        {h.failures ? <span className="text-rose-700"> (فشل: {h.failures})</span> : null}
                        <div className="text-[10px] text-[#8B8780]">رسائل فردية (قديم)</div>
                      </>
                    )}
                  </td>
                  <td className="p-4">
                    <Badge
                      className={`rounded-full ${
                        ["completed", "sent"].includes(h.status)
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {STATUS_AR[h.status] || h.status}
                    </Badge>
                    {h.resend_broadcast_id && (
                      <button
                        type="button"
                        data-testid={`broadcast-refresh-status-${h.id}`}
                        onClick={() => refreshStatus(h)}
                        className="ms-2 text-[10px] text-[#0A4D3C] underline"
                      >
                        تحديث
                      </button>
                    )}
                  </td>
                  <td className="p-4 text-xs text-[#5E5C58]">{fmt(h.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { AdminBroadcast };
